import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Tabs,
  Tab,
  Button,
  Typography,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { DropdownIcon } from 'assets/svgComponents/DropdownIcon';
import { IAppointmentCounts } from '../types';
import { useStyles } from '../styles';

interface DrugTestingTabsProps {
  activeTab: number;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  counts: IAppointmentCounts;
  onBookAppointment: () => void;
  onUploadResults: () => void;
}

export const DrugTestingTabs: React.FC<DrugTestingTabsProps> = ({
  activeTab,
  onTabChange,
  counts,
  onBookAppointment,
  onUploadResults,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const createTabLabel = (text: string, count: number) => (
    <Box className={classes.customTabLabel}>
      <Typography component="span">{text}</Typography>
      <Box className={`${classes.customCountBadge} custom-count-badge`}>
        {count < 10 ? `0${count}` : count}
      </Box>
    </Box>
  );

  const tabOptions = [
    {
      value: 0,
      key: 'appointments',
      label: t('myDrugTesting.tabs.appointments'),
      count: counts.appointments,
    },
    {
      value: 1,
      key: 'third-party-results',
      label: t('myDrugTesting.tabs.thirdPartyResults'),
      count: counts.thirdPartyResults,
    },
  ];

  const handleDropdownChange = (event: any) => {
    onTabChange(event, event.target.value);
  };

  return (
    <Box className={classes.tabsContainer} data-testid="my-drug-testing-tabs-container">
      {isMobile ? (
        <Select
          value={activeTab}
          onChange={handleDropdownChange}
          className={classes.tabDropdown}
          data-testid="my-drug-testing-tab-dropdown"
          IconComponent={DropdownIcon}
          renderValue={value => {
            const selected = tabOptions.find(option => option.value === value);
            return <Typography component="span">{selected?.label}</Typography>;
          }}
        >
          {tabOptions.map(option => (
            <MenuItem
              key={option.value}
              value={option.value}
              data-testid={`my-drug-testing-tab-dropdown-item-${option.key}`}
              className={classes.dropdownMenuItem}
            >
              <Typography component="span">{option.label}</Typography>
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Tabs value={activeTab} onChange={onTabChange} className={classes.tabs} data-testid="my-drug-testing-tabs">
          <Tab
            label={createTabLabel(t('myDrugTesting.tabs.appointments'), counts.appointments)}
            className={classes.tab}
            data-testid="my-drug-testing-tab-appointments"
          />
          <Tab
            label={createTabLabel(t('myDrugTesting.tabs.thirdPartyResults'), counts.thirdPartyResults)}
            className={classes.tab}
            data-testid="my-drug-testing-tab-third-party-results"
          />
        </Tabs>
      )}

      <Box className={classes.actionButtons}>
        <Button
          startIcon={<AddIcon />}
          className={classes.bookButton}
          data-testid="book-appointment-button"
          onClick={onBookAppointment}
        >
          {t('myDrugTesting.actions.bookAppointment')}
        </Button>
        <Button
          startIcon={<AddIcon />}
          className={classes.uploadButton}
          data-testid="upload-results-button"
          onClick={onUploadResults}
        >
          {t('myDrugTesting.actions.uploadResults')}
        </Button>
      </Box>
    </Box>
  );
};
